import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckSquare, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';

import type { CatalogPermissionDto } from './types';

const NS = 'settings';

interface PermissionGroup {
  category: string;
  rows: CatalogPermissionDto[];
}

interface PermissionChecklistProps {
  catalog: CatalogPermissionDto[];
  value: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
}

/**
 * Controlled checkbox list over the party permission catalog, grouped by
 * category. Labels reuse the exact i18n conventions of RoleEditPage
 * (`efs.settings.roles.permissions.*` / `…permission_descriptions.*` /
 * `…categories.*`) with the DTO text as fallback.
 */
export const PermissionChecklist = ({
  catalog,
  value,
  onChange,
  disabled,
}: PermissionChecklistProps) => {
  const { t } = useTranslation(NS);

  const groups = useMemo<PermissionGroup[]>(() => {
    const map = new Map<string, CatalogPermissionDto[]>();
    catalog.forEach((permission) => {
      const rows = map.get(permission.category) ?? [];
      rows.push(permission);
      map.set(permission.category, rows);
    });
    return Array.from(map.entries()).map(([category, rows]) => ({
      category,
      rows: rows.sort((a, b) => a.sort_order - b.sort_order),
    }));
  }, [catalog]);

  const permissionLabel = (permission: CatalogPermissionDto) =>
    t(`efs.settings.roles.permissions.${permission.permission_code}`, {
      defaultValue: permission.display_name || permission.permission_code,
    });
  const permissionDescription = (permission: CatalogPermissionDto) =>
    t(`efs.settings.roles.permission_descriptions.${permission.permission_code}`, {
      defaultValue: permission.description || '',
    });

  const toggle = (permissionCode: string, checked: boolean) => {
    if (checked) onChange(Array.from(new Set([...value, permissionCode])).sort());
    else onChange(value.filter((code) => code !== permissionCode));
  };

  const setCategory = (rows: CatalogPermissionDto[], checked: boolean) => {
    const codes = rows.map((permission) => permission.permission_code);
    if (checked) onChange(Array.from(new Set([...value, ...codes])).sort());
    else onChange(value.filter((code) => !codes.includes(code)));
  };

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <section key={group.category} className="rounded-md border border-efs-border">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-efs-border bg-efs-bg-subtle px-3 py-2">
            <span className="text-xs font-semibold text-efs-text-muted">
              {t(`efs.settings.roles.categories.${group.category}`, {
                defaultValue: group.category,
              })}
            </span>
            <span className="flex gap-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={disabled}
                onClick={() => setCategory(group.rows, true)}
              >
                <CheckSquare className="h-3.5 w-3.5" />
                {t('efs.settings.roles.actions.select_feature_all')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={disabled}
                onClick={() => setCategory(group.rows, false)}
              >
                <XCircle className="h-3.5 w-3.5" />
                {t('efs.settings.roles.actions.clear_feature_all')}
              </Button>
            </span>
          </div>
          <div className="divide-y divide-efs-border">
            {group.rows.map((permission) => (
              <label
                key={permission.permission_code}
                className="flex cursor-pointer items-start gap-2 px-3 py-2 hover:bg-efs-bg-subtle"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4"
                  checked={value.includes(permission.permission_code)}
                  disabled={disabled}
                  onChange={(event) => toggle(permission.permission_code, event.target.checked)}
                />
                <span className="min-w-0">
                  <span className="block text-sm text-efs-text-primary">
                    {permissionLabel(permission)}
                  </span>
                  <span className="mt-0.5 block text-xs text-efs-text-muted">
                    {permissionDescription(permission) || permission.permission_code}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};
